import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Chip, Container, Grid, LinearProgress, Paper, Stack, Typography } from '@mui/material';
import { CheckCircle as CheckCircleIcon, CircleOutlined as CircleOutlinedIcon, LockOutlined as LockOutlinedIcon, AutoAwesomeOutlined as AutoAwesomeOutlinedIcon } from '@mui/icons-material';
import Button from '../../components/Button';
import PageHeader from '../../components/PageHeader';
import { track } from '../../lib/analytics';
import { navigate } from '../../lib/navigation';
import { opportunityProfileCompletion } from '../../lib/opportunityPreferences';

const PREVIEWS = [
  ['Brand Campaign', 'Create original content for a future partner.'],
  ['UGC Opportunity', 'Produce authentic short-form brand content.'],
  ['Event Invitation', 'Connect with brands through future experiences.'],
];

function BrandOpportunitiesPage({ creator }) {
  const completion = opportunityProfileCompletion(creator);
  const [initialCompletionPercentage] = useState(() => opportunityProfileCompletion(creator).percentage);
  useEffect(() => { track('brand_opportunities_viewed', { label: `${initialCompletionPercentage}_percent_complete` }); }, [initialCompletionPercentage]);

  const goToProfile = () => {
    track('opportunity_profile_cta_clicked', { path: '/dashboard/opportunities', label: `${completion.percentage}_percent_complete` });
    navigate('/dashboard/profile#opportunity-profile');
  };
  const checklist = [
    ['Opportunity Preferences', completion.sections.preferences],
    ['Collaboration Wishlist', completion.sections.wishlist],
    ['Creator Goals', completion.sections.goals],
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <PageHeader title="Brand Opportunities" description="A future home for campaigns and collaborations relevant to your creator profile." />
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 3, overflow: 'hidden', position: 'relative', mb: 3 }}>
        <Stack spacing={1} sx={{ textAlign: 'center', alignItems: 'center', mb: 3 }}>
          <AutoAwesomeOutlinedIcon sx={{ color: 'primary.main', fontSize: 32 }} />
          <Typography variant="h4">Opportunities matched to you</Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 720 }}>
            We're working on a new way for creators to discover brand campaigns and collaboration opportunities that match their profile, interests and preferences.
          </Typography>
        </Stack>
        <Box sx={{ position: 'relative' }}>
          <Grid container spacing={2} aria-hidden="true" sx={{ filter: 'blur(3px)', opacity: 0.58, userSelect: 'none' }}>
            {PREVIEWS.map(([title, text]) => (
              <Grid size={{ xs: 12, sm: 4 }} key={title}>
                <Card variant="outlined" sx={{ height: '100%' }}><CardContent><Box sx={{ width: 40, height: 40, borderRadius: 2, bgcolor: 'primary.light', mb: 2 }} /><Typography fontWeight={700}>{title}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{text}</Typography><Box sx={{ height: 8, width: '70%', bgcolor: 'grey.200', borderRadius: 9, mt: 2 }} /></CardContent></Card>
              </Grid>
            ))}
          </Grid>
          <Stack sx={{ position: 'absolute', inset: 0, alignItems: 'center', justifyContent: 'center' }}>
            <Chip icon={<LockOutlinedIcon />} label="Coming soon" color="primary" sx={{ fontSize: '1rem', px: 1, height: 44, boxShadow: 3 }} />
          </Stack>
        </Box>
      </Paper>

      <Card variant="outlined">
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6">{completion.isComplete ? 'Your Collaboration Preferences are ready' : "Help us understand what you're looking for"}</Typography>
              <Typography color="text.secondary" sx={{ mt: 0.75, maxWidth: 680 }}>
                {completion.isComplete
                  ? "We'll use these preferences to help personalize future opportunities on CreatorCV."
                  : 'Complete your Collaboration Preferences so we can better understand the types of collaborations that may be relevant to you as CreatorCV grows.'}
              </Typography>
              <Stack spacing={1} sx={{ mt: 2 }}>
                {checklist.map(([label, complete]) => (
                  <Stack key={label} direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {complete ? <CheckCircleIcon color="success" fontSize="small" /> : <CircleOutlinedIcon color="disabled" fontSize="small" />}
                    <Typography variant="body2" fontWeight={complete ? 650 : 400}>{label}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Box sx={{ minWidth: { md: 230 }, alignSelf: { md: 'center' } }}>
              <Typography variant="body2" fontWeight={700}>{completion.completed} of 3 completed</Typography>
              <LinearProgress variant="determinate" value={completion.percentage} sx={{ my: 1.25, height: 7, borderRadius: 99 }} />
              <Button onClick={goToProfile} fullWidth>{completion.isComplete ? 'Edit Collaboration Preferences' : 'Complete Collaboration Preferences'}</Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Container>
  );
}

export default BrandOpportunitiesPage;
